// @ds/data/auditChanges — shared change-derivation for the audit activity
// feed and audit pages.
//
// Both consumers turn an `entity_audit_log` row into the same three things:
// a list of before/after field changes, a partition of those changes into
// "important" vs "other", and a coarse activity kind (created / edited /
// deleted / paid / synced / custom) used to pick an icon + headline. Landing
// the logic here once means the activity feed (`sharedSections.Bodies.tsx`)
// and the global `/audit-log` page (`src/pages/audit-log/auditLogUtils.ts`)
// can't drift into rendering different diffs for the same row.
//
// Works on BOTH row shapes the audit trigger has ever produced:
//   - new delta rows — UPDATE stores only the changed keys in before_value/
//     after_value, plus `changed_fields` naming them in order.
//   - legacy full-snapshot rows — UPDATE stores the entire row before/after;
//     `changed_fields` is null, so we fall back to a client-side
//     JSON-inequality diff over the union of keys, minus the same columns
//     the trigger itself has always globally ignored.

import type { AuditAction } from "@shared/types/audit";

export interface AuditChangeRowLike {
  action: AuditAction;
  actor_id?: string | null;
  before_value: Record<string, unknown> | null;
  after_value: Record<string, unknown> | null;
  changed_fields?: string[] | null;
}

export interface DerivedChange {
  field: string;
  before: unknown;
  after: unknown;
}

/**
 * Columns a legacy client-side diff ignores — mirrors the audit trigger's
 * own global ignore list, so diffing a full-snapshot row agrees with what a
 * delta row would have recorded for the same write.
 */
export const AUDIT_DIFF_SKIP_FIELDS: ReadonlySet<string> = new Set([
  "id",
  "created_at",
  "updated_at",
  "search_vector",
]);

/**
 * Derive the field-level changes for one audit row.
 *
 * INSERT and DELETE rows render as milestones in the feed, not field diffs —
 * both always return []. For UPDATE:
 *
 *   - delta rows (`changed_fields` non-empty): one DerivedChange per entry
 *     of `changed_fields`, in that order.
 *   - legacy rows (`changed_fields` null/empty): union of before_value +
 *     after_value keys, minus AUDIT_DIFF_SKIP_FIELDS, keeping only pairs
 *     where JSON.stringify(before) !== JSON.stringify(after), sorted
 *     alphabetically by field.
 *
 * Both branches null-coalesce a missing/absent projection to `null`.
 */
export function deriveChanges(row: AuditChangeRowLike): DerivedChange[] {
  if (row.action !== "UPDATE") return [];

  if (row.changed_fields && row.changed_fields.length > 0) {
    return row.changed_fields.map((field) => ({
      field,
      before: row.before_value?.[field] ?? null,
      after: row.after_value?.[field] ?? null,
    }));
  }

  const allKeys = new Set<string>();
  if (row.before_value) Object.keys(row.before_value).forEach((k) => allKeys.add(k));
  if (row.after_value) Object.keys(row.after_value).forEach((k) => allKeys.add(k));

  const changes: DerivedChange[] = [];
  for (const field of allKeys) {
    if (AUDIT_DIFF_SKIP_FIELDS.has(field)) continue;
    const before = row.before_value?.[field] ?? null;
    const after = row.after_value?.[field] ?? null;
    if (JSON.stringify(before) === JSON.stringify(after)) continue;
    changes.push({ field, before, after });
  }

  changes.sort((a, b) => a.field.localeCompare(b.field));
  return changes;
}

/**
 * Split changes into "important" vs "other" by field-name membership.
 *
 * `importantFields` undefined or empty means the importance feature is off
 * for the caller (e.g. no starter list configured for that entity) — every
 * change is treated as important and none are hidden. Otherwise a stable
 * partition: relative order is preserved within each group.
 */
export function partitionByImportance(
  changes: DerivedChange[],
  importantFields?: readonly string[],
): { important: DerivedChange[]; other: DerivedChange[] } {
  if (!importantFields || importantFields.length === 0) {
    return { important: changes, other: [] };
  }

  const importantSet = new Set(importantFields);
  const important: DerivedChange[] = [];
  const other: DerivedChange[] = [];
  for (const change of changes) {
    (importantSet.has(change.field) ? important : other).push(change);
  }
  return { important, other };
}

export type AuditActivityKind =
  | "created"
  | "edited"
  | "deleted"
  | "paid"
  | "synced"
  | "custom";

/**
 * Classify a row into a coarse activity kind for icon/headline choice.
 *
 * Priority order (first match wins):
 *   1. action INSERT            → "created"
 *   2. action DELETE            → "deleted"
 *   3. `is_deleted` changed     → after === true ? "deleted" : "custom" (restore)
 *   4. `payment_status` changed → "paid"
 *   5. actor_id == null (system write, no higher-priority match) → "synced"
 *   6. else                     → "edited"
 */
export function inferKind(row: AuditChangeRowLike, changes: DerivedChange[]): AuditActivityKind {
  if (row.action === "INSERT") return "created";
  if (row.action === "DELETE") return "deleted";

  const isDeletedChange = changes.find((c) => c.field === "is_deleted");
  if (isDeletedChange) return isDeletedChange.after === true ? "deleted" : "custom";

  const paymentStatusChanged = changes.some((c) => c.field === "payment_status");
  if (paymentStatusChanged) return "paid";

  if (row.actor_id == null) return "synced";

  return "edited";
}

/**
 * Format a value for display in a before/after diff cell.
 *
 * EXACT semantics of `formatDiffValue` in
 * `src/pages/audit-log/auditLogUtils.ts` — kept identical so the activity
 * feed and the global audit page render the same text for the same value.
 */
export function formatChangeValue(
  value: unknown,
  boolLabels?: { true: string; false: string },
): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") {
    return value ? (boolLabels?.true ?? "Tak") : (boolLabels?.false ?? "Nie");
  }
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "string") return value;
  return String(value as number | bigint | symbol);
}
